import Button from 'tio-reimagined/components/button';
import TooltipButton from 'tio-reimagined/components/tooltip-button';
import { htmlSafe } from '@ember/template';
import { divide } from 'tio-ui/math';
import { dropTask } from 'ember-concurrency';
import { fn } from '@ember/helper';
import { Listbox } from 'tio-ui/components/collections';
import Modal from 'tio-reimagined/components/modal';
import Footer from 'tio-reimagined/components/footer';
import { not, gt, or, eq, and } from 'tio-ui/utilities';
import { on } from '@ember/modifier';
import {
  PhSparkle,
  PhChatCenteredText,
  PhBuilding,
  PhCurrencyCircleDollar,
  PhFile,
  PhCalendarDots,
  PhStudent,
  PhDotsThree,
  PhXCircle,
  PhArrowUUpLeft,
  PhHandDeposit,
  PhUserSwitch,
  PhArrowsCounterClockwise,
  PhClockCounterClockwise,
  PhCreditCard,
  PhShieldWarning,
} from 'ember-phosphor-icons';
import type IntlService from 'ember-intl/services/intl';
import type SessionContextService from '../../../services/session-context.ts';
import Popover from 'tio-reimagined/components/popover';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import Textarea from 'tio-reimagined/components/textarea';
import { cached } from '@glimmer/tracking';
import { tracked } from '@glimmer/tracking';
import AlertBanner from 'tio-reimagined/components/alert-banner';
import ApproveModal from './approve-modal.gts';
import Component from '@glimmer/component';
import safeFormatNumber from '../../../helpers/safe-format-number';
import SelectTextArea from '../../tio/select-text-area.gts';
import TioErrorMessages from '../../tio/error-messages.gts';
import {
  getSingleValueForTasField,
  getSortedOptionsByFieldName,
  setSingleValueForTasField,
  type TemplateFieldName,
} from '../../../utils/tuition-assistance/fields.ts';
import { setSemesterCodeString } from '../../../utils/format.ts';
import type {
  ApplicationApprovalSummary,
  ApplicationApprovalSummaryApprover,
} from '../../../types/tuition-assistance';
import type FeaturesService from '../../../services/features';
import type TuitionAssistanceService from '../../../services/tuition-assistance.ts';
import type Owner from '@ember/owner';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type TasApplicationModel from '../../../models/tas-application';
import type RoleView from '../../../models/role-view';

/** System/internal fields that should not be validated as user-fillable */
const SYSTEM_FIELDS = new Set([
  'APPLICATION_ID',
  'PRE_APPROVED_TOTAL',
  'FINAL_APPROVED_TOTAL',
  'CARD_PRE_APPROVED_TOTAL',
  'CARD_FINAL_APPROVED_TOTAL',
  'APPROVED_COURSE_TOTAL',
  'WAIVE_EVIDENCE_ATTACHMENT_REQUIREMENT',
  'WAIVE_EVIDENCE_APPROVAL_REQUIREMENT',
  'EVIDENCE_APPROVAL_WAIVED_BY',
  'SCHOLARSHIPS_RECEIVED',
  'CLAIMS_FINANCIALS',
  'GRADE_POINT_AVERAGE',
  'PAYMENT_TYPE_FOR_DIRECT_PAY',
  // Template-level config fields (PROGRAM province, not employee-fillable)
  'TYPE',
  'PROGRAM_CODE',
  'PROGRAM_NAME',
  'PROGRAM_TYPE',
  'PROGRAM_DESCRIPTION',
  'PAYROLL_EXPECTATION',
  'CREATE_SIGNATURE_FLOW',
  'CLOSE_PROGRAM_BASED_ON',
  'INSTITUTION_LIST_SUPPLIED',
  'INSTITUTION_LIST_IS_EXCLUSIVE',
  'MAXIMUM_ANNUAL_REIMBURSEMENT',
  'MAXIMUM_LIFETIME_REIMBURSEMENT',
  'MAXIMUM_ANNUAL_REIMBURSEMENT_BY_EMPLOYMENT_TYPE',
  'ALLOW_UNACCREDITED_INSTITUTIONS',
  'ALLOW_PRE_APPROVAL_SUBMISSION_BEYOND_LIMIT',
  'ALLOW_WAIVER_OF_EVIDENCE_APPROVAL',
  'LOCK_EMPLOYEE_STATUS_BASED_ON',
  'CALCULATE_TOWARDS_TOTAL_BASED_ON',
  'COMPLETION_DOCUMENTS_STRING',
  'COURSE_APPROVAL_DOCUMENTS_STRING',
  'COURSE_INFORMATION',
  'YOUR_INFORMATION',
  'SCHOLARSHIP_NAME',
  'SCHOLARSHIP_AMOUNT',
  // Auto-computed at submit time from COURSES_BEGIN_DATE
  'SEMESTER_CODE',
]);

/**
 * Footer action bar for TAS approval page
 * Contains Approve button, drawer navigation icons, and more actions menu
 *
 * Layout:
 * - Desktop: Horizontal row with all buttons
 * - Mobile: Stacked layout (Approve button on top, icon grid below)
 */
interface ApprovalFooterSignature {
  Element: HTMLDivElement;
  Args: {
    /** Callback when drawer should open */
    onDrawerOpen: (drawerType: string) => void;
    /** Currently active drawer type (for visual feedback) */
    activeDrawer?: string | null;
    /** Whether approve button should show loading state */
    approveButtonLoading?: boolean;
    /** TAS Application model - needed for approval workflows */
    application: TasApplicationModel;
    /** Consuming app context - optional, will be computed from FeaturesService */
    consumingApp?: string;
    /** Whether the application is currently being edited (disables approve button) */
    isEditingApplication?: boolean;
    /** Query param to enable AI analysis in production (tio-admin only) */
    ai?: boolean;
    /** Dynamic expense type label (e.g., "Courses", "Course Cash") */
    expenseType?: string;
    /** Optional callback to open the CourseCash drawer (admin-only) */
    onCourseCashOpen?: () => void;
  };
}

class ApprovalFooter extends Component<ApprovalFooterSignature> {
  @service declare store: StoreService;
  @service declare features: FeaturesService;
  @service declare intl: IntlService;
  @service declare tuitionAssistance: TuitionAssistanceService;
  @service declare sessionContext: SessionContextService;

  @tracked isRejectModalOpen = false;
  @tracked rejectNote = '';
  @tracked rejectError = null;
  @tracked requestedRepaymentAmount = 0;

  // Approve modal state
  @tracked isApproveModalOpen = false;

  // Submit for User state
  @tracked submitForUserModalOpen = false;
  @tracked submitForUserError: string | null = null;

  // Rescind state
  @tracked isRescindModalOpen = false;
  @tracked rescindError: string | null = null;

  // Request Change state
  @tracked isRequestChangeModalOpen = false;
  @tracked requestChangeNote = '';
  @tracked requestChangeError: string | null = null;

  // Reassign state
  @tracked selectedApproverToReassign: ApplicationApprovalSummaryApprover | null = null;
  @tracked isReassignModalOpen = false;

  // Reopen state
  @tracked isReopenModalOpen = false;
  @tracked reopenError: string | null = null;

  // Policy Override state
  @tracked isOverrideModalOpen = false;

  // Popover state - control the more actions popover
  @tracked isMoreActionsPopoverOpen = false;

  constructor(owner: Owner, args: ApprovalFooterSignature['Args']) {
    super(owner, args);

    // Initialize repayment amount for rejection modal
    this.requestedRepaymentAmount = args.application.canRequestRepayment
      ? args.application.paidTotal
      : 0;
  }

  /**
   * Determines if AI analysis feature should be shown
   * In tio-admin:
   * - Non-production: Enabled with feature flag
   * - Production: Enabled with feature flag AND ?ai=true query param
   */
  /** Whether to show the CourseCash speed dial icon */
  get showCourseCash() {
    return (
      this.args.application.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH' &&
      !!this.args.onCourseCashOpen
    );
  }

  get showAiAnalysis(): boolean {
    if (!this.features.isAdminApp) {
      return false;
    }

    const isProduction = this.features.tioEnvironment === 'production';
    const hasAiFeatureFlag = this.features.isEnabled('aiAnalysis');
    const hasAiQueryParam = this.args.ai === true;

    // In production, require both feature flag and query param
    if (isProduction) {
      return hasAiFeatureFlag && hasAiQueryParam;
    }

    // In non-production, just need feature flag
    return hasAiFeatureFlag;
  }

  /**
   * Compute consuming app from FeaturesService or use passed-in value
   * Maps modulePrefix to consumingApp identifier
   */
  get computedConsumingApp(): string {
    // If consumingApp is explicitly passed, use it
    if (this.args.consumingApp) {
      return this.args.consumingApp;
    }

    // Otherwise, derive from FeaturesService
    if (this.features.isAdminApp) {
      return 'tio-admin';
    } else if (this.features.isEmployeeApp) {
      return 'tio-employee';
    } else if (this.features.isAgentApp) {
      return 'tio-agent';
    }

    return 'unknown';
  }

  /**
   * Get the current approval queue based on application state
   * Focuses on TasApplication approval states
   */
  get approvalQueue(): ApplicationApprovalSummary {
    const { application } = this.args;

    if (application.state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL') {
      return application.evidenceApproval;
    } else if (application.state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL') {
      return application.coursesApproval;
    } else {
      return {
        affirmed: [],
        current_reviewers: [],
        possible_reviewers: [],
        required: [],
        approved: false,
      };
    }
  }

  /**
   * Determines if reassign approver action should be available
   * Shows when application is in a state with a pending approval queue
   */
  get canReassignApprover(): boolean {
    const approvalQueue = this.approvalQueue;
    // Show if there are possible reviewers to reassign
    return approvalQueue.possible_reviewers?.length > 0;
  }

  /**
   * Get company ID for scoping approver search.
   *
   * The `application` arg is typed as `TasApplicationModel` on this component,
   * so we go through its `tasProgramInstance` relationship. A prior version of
   * this getter branched on `modelName === 'tas-program-instance'` but that
   * branch was unreachable under the current signature.
   */
  get companyId(): string {
    return this.args.application.tasProgramInstance.employee.company.id ?? '';
  }

  /**
   * Determines if rejection action should be enabled
   * Only enabled for applications in pending approval states
   */
  get canRejectApplication() {
    const state = this.args.application.state;
    return (
      state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL' ||
      state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL'
    );
  }

  /**
   * Determines if Request Change action should be enabled
   * Same conditions as rejection - only for pending approval states
   */
  get canRequestChange() {
    return this.canRejectApplication; // Same logic as reject
  }

  /**
   * Determines if Approve action should be enabled
   * Disabled if:
   * - Application is being edited (unsaved changes)
   * - Application is not in a pending approval state
   */
  get canApproveApplication() {
    // Disable if currently editing the application
    if (this.args.isEditingApplication) {
      return false;
    }

    const state = this.args.application.state;
    return state.includes('PENDING');
  }

  /**
   * Determines the correct adapter method for Request Change action
   * Returns method name based on application state:
   * - PENDING_COURSES_APPROVAL → 'requestChanges'
   * - PENDING_EVIDENCE_APPROVAL → 'requestCourseEvidenceChanges'
   */
  get requestChangeAdapterMethod(): string {
    const state = this.args.application.state;

    if (state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL') {
      return 'requestChanges';
    } else if (state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL') {
      return 'requestCourseEvidenceChanges';
    }

    return 'requestChanges'; // Default fallback
  }

  /**
   * Get canned response notes for Request Change action
   * Fetches pre-defined response text from company comments
   */
  get tasResponseNotes() {
    const partnerComments = this.args.application.tasProgramInstance.employee.company.comments;
    return partnerComments
      .filter((comment) => comment.isTasApproval)
      .map((comment) => comment.tasApprovalNote);
  }

  /**
   * Get response template field name based on approval action
   */
  private getFieldNameForAction(action: 'reject' | 'requestChange'): TemplateFieldName | null {
    const state = this.args.application.state;

    if (state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL') {
      return action === 'reject'
        ? ('EVIDENCE_APPROVAL_REJECT_TEXT' as TemplateFieldName)
        : ('EVIDENCE_APPROVAL_REVISE_TEXT' as TemplateFieldName);
    }

    if (state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL') {
      return action === 'reject'
        ? ('COURSES_APPROVAL_REJECT_TEXT' as TemplateFieldName)
        : ('COURSES_APPROVAL_REVISE_TEXT' as TemplateFieldName);
    }

    return null;
  }

  /**
   * Get the TAS response tag for the given action based on application state.
   */
  private getTagForAction(action: 'reject' | 'requestChange'): string | null {
    const state = this.args.application.state;

    if (state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL') {
      return action === 'reject' ? 'TAS.EvidenceApproval.REJECT' : 'TAS.EvidenceApproval.REVISE';
    }

    if (state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL') {
      return action === 'reject' ? 'TAS.CourseApproval.REJECT' : 'TAS.CourseApproval.REVISE';
    }

    return null;
  }

  /**
   * Get company comments filtered by a specific TAS response tag.
   */
  private companyCommentsForTag(tag: string): { title: string; body: string }[] {
    const partnerComments =
      this.args.application.tasProgramInstance?.employee?.company?.comments ?? [];
    return partnerComments
      .filter((comment) => comment.tags?.includes(tag))
      .map((comment) => comment.tasApprovalNote);
  }

  /**
   * Get template text for reject comment.
   * Single combined option (template + company) → pre-fills textarea.
   * Falls back to legacy fields value.
   */
  get templateRejectComment() {
    const templateOptions = this.rejectResponseOptions;
    const tag = this.getTagForAction('reject');
    const companyOptions = tag ? this.companyCommentsForTag(tag) : [];
    const allOptions = [
      ...templateOptions.map((o) => ({ title: o.label, body: o.value })),
      ...companyOptions,
    ];

    // New system: single combined option → pre-fill textarea
    if (allOptions.length === 1) {
      return allOptions[0]?.body ?? '';
    }

    // Old system fallback
    const fields = this.args.application.tasProgramInstance?.tasProgramTemplate?.fields;
    if (!fields) {
      return '';
    }

    const fieldName = this.getFieldNameForAction('reject');
    if (!fieldName) {
      return '';
    }

    const value = getSingleValueForTasField(fieldName, fields);
    return typeof value === 'string' ? value : '';
  }

  /**
   * Get template text for request-change comment.
   * Single combined option (template + company) → pre-fills textarea.
   * Falls back to legacy fields value.
   */
  get templateRequestChangeComment() {
    const templateOptions = this.requestChangeResponseOptions;
    const tag = this.getTagForAction('requestChange');
    const companyOptions = tag ? this.companyCommentsForTag(tag) : [];
    const allOptions = [
      ...templateOptions.map((o) => ({ title: o.label, body: o.value })),
      ...companyOptions,
    ];

    // New system: single combined option → pre-fill textarea
    if (allOptions.length === 1) {
      return allOptions[0]?.body ?? '';
    }

    // Old system fallback
    const fields = this.args.application.tasProgramInstance?.tasProgramTemplate?.fields;
    if (!fields) {
      return '';
    }

    const fieldName = this.getFieldNameForAction('requestChange');
    if (!fieldName) {
      return '';
    }

    const value = getSingleValueForTasField(fieldName, fields);
    return typeof value === 'string' ? value : '';
  }

  /**
   * Get response template options for reject action
   */
  get rejectResponseOptions() {
    const fieldName = this.getFieldNameForAction('reject');
    if (!fieldName) {
      return [];
    }

    const tasFieldOptions =
      this.args.application.tasProgramInstance?.tasProgramTemplate?.tasFieldOptions ?? [];

    return getSortedOptionsByFieldName(fieldName, tasFieldOptions);
  }

  /**
   * Get response template options for request-change action
   */
  get requestChangeResponseOptions() {
    const fieldName = this.getFieldNameForAction('requestChange');
    if (!fieldName) {
      return [];
    }

    const tasFieldOptions =
      this.args.application.tasProgramInstance?.tasProgramTemplate?.tasFieldOptions ?? [];

    return getSortedOptionsByFieldName(fieldName, tasFieldOptions);
  }

  /**
   * Convert reject response template options to SelectTextArea format.
   * Merges program template options with company comments tagged for this action.
   * 2+ combined options → dropdown. 1 → pre-fill. 0 → fallback to all canned notes.
   */
  get rejectTextAreaOptions() {
    const templateOptions = this.rejectResponseOptions;
    const tag = this.getTagForAction('reject');
    const companyOptions = tag ? this.companyCommentsForTag(tag) : [];
    const allOptions = [
      ...templateOptions.map((option) => ({ title: option.label, body: option.value })),
      ...companyOptions,
    ];

    if (allOptions.length > 1) {
      return allOptions;
    }

    // Exactly 1 combined option: no dropdown; textarea is pre-filled via templateRejectComment
    if (allOptions.length === 1) {
      return [];
    }

    // Fallback to canned notes if no options configured
    return this.tasResponseNotes;
  }

  /**
   * Convert request-change response template options to SelectTextArea format.
   * Merges program template options with company comments tagged for this action.
   * 2+ combined options → dropdown. 1 → pre-fill. 0 → fallback to all canned notes.
   */
  get requestChangeTextAreaOptions() {
    const templateOptions = this.requestChangeResponseOptions;
    const tag = this.getTagForAction('requestChange');
    const companyOptions = tag ? this.companyCommentsForTag(tag) : [];
    const allOptions = [
      ...templateOptions.map((option) => ({ title: option.label, body: option.value })),
      ...companyOptions,
    ];

    if (allOptions.length > 1) {
      return allOptions;
    }

    // Exactly 1 combined option: no dropdown; textarea is pre-filled via templateRequestChangeComment
    if (allOptions.length === 1) {
      return [];
    }

    // Fallback to canned notes if no options configured
    return this.tasResponseNotes;
  }

  /**
   * Whether the application uses Course Cash payment method
   */
  get isCourseCashPayment() {
    return this.args.application.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH';
  }

  /**
   * Whether the employee has completed their cardholder profile (accepted T&C + DOB)
   */
  get employeeHasCardholder() {
    return (this.args.application.tasProgramInstance?.employee?.pcsCardHolders?.length ?? 0) > 0;
  }

  /**
   * Determines if "Submit for User" action should be available in the menu
   * Available when:
   * 1. Application is in ATTEND or DEFINE_COURSES (DRAFT) state
   * 2. User has approval permissions
   *
   * Note: Missing requirements (courses, fields, attachments) are shown in the modal
   * rather than preventing the menu item from being clickable.
   */
  get canSubmitForUser() {
    const state = this.args.application.state;

    const allowedStates = ['TAS.ApplicationState.ATTEND', 'TAS.ApplicationState.DEFINE_COURSES'];

    return allowedStates.includes(state) && this.hasApprovalPermissions;
  }

  /**
   * Whether submit-for-user should be blocked for DRAFT course cash apps
   * without a cardholder profile. The employee must accept T&C and enter DOB first.
   */
  get isDraftCourseCashWithoutCardholder() {
    return (
      this.args.application.state === 'TAS.ApplicationState.DEFINE_COURSES' &&
      this.isCourseCashPayment &&
      !this.employeeHasCardholder
    );
  }

  /**
   * Determines which adapter method to call when submitting for user
   * - ATTEND state: always requestCourseEvidence (evidence submission after attending)
   * - DEFINE_COURSES (DRAFT) state: depends on program type
   *   - Type 2 / 4.2 → requestCourseEvidence
   *   - Type 3 / 4.3 → requestCoursesApproval
   */
  get submitForUserAdapterMethod(): string {
    const state = this.args.application.state;

    // ATTEND state always submits evidence (existing behavior)
    if (state === 'TAS.ApplicationState.ATTEND') {
      return 'requestCourseEvidence';
    }

    // DEFINE_COURSES (DRAFT): route based on type classification
    const typeClassification = this.args.application.typeClassification;
    const actionMap: Record<string, string> = {
      'TAS.ProgramType.2': 'requestCourseEvidence',
      'TAS.ProgramType.3': 'requestCoursesApproval',
      'TAS.ProgramType.4.2': 'requestCourseEvidence',
      'TAS.ProgramType.4.3': 'requestCoursesApproval',
    };

    return actionMap[typeClassification] || 'requestCourseEvidence';
  }

  /**
   * Check if user has permissions to submit on behalf of employee
   */
  get hasApprovalPermissions(): boolean {
    const user = this.sessionContext.user;

    return Boolean(
      user?.isEmployeeAdmin ||
      user?.isAccountOwner ||
      user?.isTioAdmin ||
      user?.isTasTioApprover ||
      (this.args.application.isPrepaidProgram && user?.isPayOps)
    );
  }

  /**
   * Whether the policy override option should be available
   * Only admins can override policies, and only if not already overridden
   */
  get canOverridePolicies() {
    return this.features.isAdminApp && !this.args.application.ignorePolicies;
  }

  /**
   * Determines if application is in a rescindable state
   * Rescindable states are application states that can be reverted
   */
  get inRescindableState() {
    const alwaysRescindableStates = new Set([
      'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL',
      'TAS.ProgramInstanceState.PENDING_COMPLETION_APPROVAL',
      'TAS.ProgramInstanceState.PENDING_WITHDRAWAL_APPROVAL',
      'TAS.ProgramInstanceState.PRIMED',
      'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
      'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
    ]);

    const state = this.args.application.state;
    const isPaid = this.args.application.isPaid;

    // Always rescindable states
    if (alwaysRescindableStates.has(state)) {
      return true;
    }

    // Special handling for ATTEND and FULFILLED
    if (state === 'TAS.ApplicationState.ATTEND' || state === 'TAS.ApplicationState.FULFILLED') {
      return !isPaid;
    }

    return false;
  }

  /**
   * Determines if user has permission to rescind the application
   * User can rescind if they have the same role as the previous approver
   */
  get userCanRescindApp() {
    const user = this.sessionContext.user;

    // TIO-ADMIN context: Superusers can rescind anything
    if (user?.isTioAdmin) {
      const userRoles: readonly RoleView[] = user?.uniqueRolesByRelationshipType || [];
      if (userRoles.some((role) => role.relationshipType === 'TAS.Admin.SUPERUSER')) {
        return true;
      }
      // Fall through to role matching for non-superuser admins
    }

    // EMPLOYEE context: Account owner or employee admin can always rescind
    if (user?.isAccountOwnerOrEmployeeAdmin) {
      return true;
    }

    // Find the approver role for role-based permission check
    const transitionLog = this.args.application.transitionLog || [];
    const currentState = this.args.application.state;

    // Determine which approval stage we're in to avoid matching approvers from earlier stages
    const isEvidenceStage =
      currentState.includes('EVIDENCE') || currentState === 'TAS.ApplicationState.FULFILLED';
    const isCoursesStage =
      currentState.includes('COURSES') || currentState === 'TAS.ApplicationState.ATTEND';

    const relevantEvents = isEvidenceStage
      ? ['request_evidence_approval', 'approve_evidence', 'decline_evidence', 'reject_evidence']
      : isCoursesStage
        ? ['request_courses_approval', 'approve_courses', 'decline_courses', 'reject_courses']
        : [];

    // Find the most recent transition with approver info for the current stage
    // Backend auto-approvals create transitions with empty reason: {} objects
    let transitionWithApprover;
    for (let i = transitionLog.length - 1; i >= 0; i--) {
      const transition = transitionLog[i];
      if (
        transition?.reason?.by?.actingWithRole?.relationshipType &&
        (relevantEvents.length === 0 || relevantEvents.includes(transition.event))
      ) {
        transitionWithApprover = transition;
        break;
      }
    }

    if (!transitionWithApprover) {
      return false; // No approver found, can't determine permission
    }

    // Safe to use non-null assertions here because loop condition guarantees these exist
    const approverRoleType = transitionWithApprover.reason!.by!.actingWithRole.relationshipType;

    const currentRole = this.sessionContext.currentRole;

    // TIO-ADMIN: Check if user has matching role
    if (user?.isTioAdmin) {
      const userRoles: readonly RoleView[] = user?.uniqueRolesByRelationshipType || [];
      return userRoles.some((role) => role.relationshipType === approverRoleType);
    }

    // EMPLOYEE: Current role must match the approver role
    return currentRole?.relationshipType === approverRoleType;
  }

  /**
   * Determines if rescind button should be shown
   * Combined visibility check for all conditions
   */
  get showRescindButton() {
    return this.inRescindableState && this.userCanRescindApp;
  }

  /**
   * Determines if application is in a rejected state
   * Used to show "Reopen Application" action instead of "Reject Application"
   */
  get isRejected(): boolean {
    const rejectedStates = [
      'TAS.ProgramInstanceState.PROGRAM_REJECTED',
      'TAS.ProgramInstanceState.COMPLETION_REJECTED',
      'TAS.ProgramInstanceState.WITHDRAWAL_REJECTED',
      'TAS.ApplicationState.COURSES_REJECTED',
      'TAS.ApplicationState.EVIDENCE_REJECTED',
      'TAS.ApplicationState.REJECTED_PER_POLICY',
    ];

    return rejectedStates.includes(this.args.application.state);
  }

  /**
   * Determines which adapter to use for reopening application
   * Returns 'tasProgramInstance' or 'tasApplication' based on state
   */
  get setAdapter(): string | undefined {
    const splitState = this.args.application.state.split('.').slice(0, 2).join('.');
    return splitState === 'TAS.ProgramInstanceState'
      ? 'tasProgramInstance'
      : splitState === 'TAS.ApplicationState'
        ? 'tasApplication'
        : undefined;
  }

  /**
   * Determines which adapter method to call for reopening
   * Maps rejected states to their corresponding adapter methods
   */
  get adapterMethod(): string {
    const actionMap: Record<string, string> = {
      'TAS.ProgramInstanceState.PROGRAM_REJECTED': 'requestChanges',
      'TAS.ProgramInstanceState.COMPLETION_REJECTED': 'requestProgramCompletionChanges',
      'TAS.ProgramInstanceState.WITHDRAWAL_REJECTED': 'requestProgramWithdrawalChanges',
      'TAS.ApplicationState.COURSES_REJECTED': 'requestChanges',
      'TAS.ApplicationState.EVIDENCE_REJECTED': 'requestCourseEvidenceChanges',
      'TAS.ApplicationState.REJECTED_PER_POLICY': 'rescindLastApproverAction',
    };
    return actionMap[this.args.application.state] || '';
  }

  /**
   * Check if last transition was auto-rejection from ATTEND state
   * Used to determine if approved total should be reset during reopen
   */
  get lastTransitionLogAutoRejectedFromAttend(): boolean {
    const log = this.args.application.transitionLog;
    const lastTransition = log?.at(-1);
    return (
      log?.length >= 2 &&
      lastTransition?.to === 'TAS.ApplicationState.EVIDENCE_REJECTED' &&
      lastTransition.from === 'TAS.ApplicationState.ATTEND'
    );
  }

  /**
   * Get the approved total before the last rejection
   * Used to reset approved total if application was auto-rejected
   */
  get lastApprovedTotalBeforeRejection(): number | undefined {
    return this.args.application.approvedTotalLog?.at(-1)?.from;
  }

  /**
   * Validate requirements for submitting on user's behalf
   * Returns array of missing requirement descriptions
   *
   * ATTEND state: validates evidence requirements (grades, evidence attachments, GPA)
   * DEFINE_COURSES (DRAFT) state: validates based on program type
   *   - Type 2 / 4.2: evidence attachments (or pre-approval attachments)
   *   - Type 3 / 4.3: pre-approval attachments
   */
  get missingRequirementsForSubmitForUser(): string[] {
    const application = this.args.application;
    const state = application.state;

    // DRAFT state: validate based on program type
    if (state === 'TAS.ApplicationState.DEFINE_COURSES') {
      return this.missingRequirementsForDraftSubmit;
    }

    // ATTEND state: existing evidence validation
    const {
      tasProgramInstance,
      isMissingCourseGrades,
      isMissingRequiredEvidence,
      requiresGPA,
      gradePointAverage,
    } = application;

    let missingRecs: string[] = [];
    const courseGradeRequired = tasProgramInstance.tasProgramTemplate.courseGradeRequired;

    if (courseGradeRequired && isMissingCourseGrades) {
      missingRecs = [...missingRecs, ' course grade(s)'];
    }

    if (isMissingRequiredEvidence) {
      missingRecs = [...missingRecs, ' evidence attachment'];
    }

    if (requiresGPA && !gradePointAverage) {
      missingRecs = [...missingRecs, ' GPA'];
    }

    return missingRecs;
  }

  /**
   * Validate requirements for DRAFT (DEFINE_COURSES) state submission
   * Dynamically checks all required template fields, custom fields, attachments, and courses
   */
  @cached
  get missingRequirementsForDraftSubmit(): string[] {
    const application = this.args.application;
    const typeClassification = application.typeClassification;
    const template = application.tasProgramInstance?.tasProgramTemplate;
    let missingRecs: string[] = [];

    // Must have at least one active course
    if (!application.activeCourses?.length) {
      missingRecs = [...missingRecs, ' at least one course'];
    }

    // Check all required template fields dynamically by province
    if (template?.fields) {
      const templateFields = template.fields as Record<
        string,
        {
          province?: string;
          required?: boolean;
          visible?: boolean;
          label?: string;
          values?: unknown[];
        }
      >;

      for (const [fieldName, config] of Object.entries(templateFields)) {
        if (!config?.required || !config?.visible || SYSTEM_FIELDS.has(fieldName)) {
          continue;
        }

        const province = config.province;

        // APPLICATION province: check application.fields
        if (province === 'APPLICATION') {
          const value = getSingleValueForTasField(
            fieldName as TemplateFieldName,
            application.fields
          );
          if (value === null || value === undefined || value === '') {
            missingRecs = [...missingRecs, ` ${config.label || fieldName}`];
          }
        }

        // APPLICANT or PROGRAM province: check instance.fields
        // PROGRAM province includes employee-fillable fields like Institution Name,
        // Program Specialization, etc. that are stored on the program instance
        if (province === 'APPLICANT' || province === 'PROGRAM') {
          const instanceFields = application.tasProgramInstance?.fields;
          const value = getSingleValueForTasField(fieldName as TemplateFieldName, instanceFields);
          if (value === null || value === undefined || value === '') {
            missingRecs = [...missingRecs, ` ${config.label || fieldName}`];
          }
        }

        // COURSE province: checked per-course below (skip here)
      }
    }

    // Check required course-province template fields on each active course
    if (template?.fields && application.activeCourses?.length) {
      const templateFields = template.fields as Record<
        string,
        {
          province?: string;
          required?: boolean;
          visible?: boolean;
          label?: string;
          values?: unknown[];
        }
      >;

      // Collect required course field names
      const requiredCourseFields = Object.entries(templateFields).filter(
        ([fieldName, config]) =>
          config?.province === 'COURSE' &&
          config?.required &&
          config?.visible &&
          !SYSTEM_FIELDS.has(fieldName)
      );

      for (const course of application.activeCourses) {
        const courseName =
          getSingleValueForTasField('COURSE_NAME', course.fields) || 'Unnamed course';

        for (const [fieldName, config] of requiredCourseFields) {
          const value = getSingleValueForTasField(fieldName as TemplateFieldName, course.fields);
          if (value === null || value === undefined || value === '') {
            missingRecs = [...missingRecs, ` ${config.label || fieldName} (${courseName})`];
          }
        }

        // Check required custom fields per course
        const courseCustomFields = course.customFields || [];
        const missingCourseCustom = courseCustomFields.filter(
          (f) =>
            f.required &&
            f.visible &&
            (!f.values?.length ||
              f.values[0] === undefined ||
              f.values[0] === null ||
              f.values[0] === '')
        );
        if (missingCourseCustom.length) {
          missingRecs = [
            ...missingRecs,
            ...missingCourseCustom.map((f) => ` ${f.label} (${courseName})`),
          ];
        }
      }
    }

    // Check required custom fields on the application
    const appCustomFields = application.customFields || [];
    const missingCustomFields = appCustomFields.filter(
      (f) =>
        f.required &&
        f.visible &&
        (!f.values?.length ||
          f.values[0] === undefined ||
          f.values[0] === null ||
          f.values[0] === '')
    );
    if (missingCustomFields.length) {
      missingRecs = [...missingRecs, ...missingCustomFields.map((f) => ` ${f.label}`)];
    }

    // Check required custom fields on the program instance
    const instanceCustomFields = application.tasProgramInstance?.customFields || [];
    const missingInstanceCustomFields = instanceCustomFields.filter(
      (f) =>
        f.required &&
        f.visible &&
        (!f.values?.length ||
          f.values[0] === undefined ||
          f.values[0] === null ||
          f.values[0] === '')
    );
    if (missingInstanceCustomFields.length) {
      missingRecs = [...missingRecs, ...missingInstanceCustomFields.map((f) => ` ${f.label}`)];
    }

    // Attachment requirements by type
    switch (typeClassification) {
      case 'TAS.ProgramType.2':
      case 'TAS.ProgramType.4.2': {
        // Type 2/4.2: needs evidence attachments OR pre-approval attachments
        const hasAttachments =
          application.hasRequiredEvidenceAttachments ||
          application.hasRequiredPreApprovalAttachments;
        if (!hasAttachments) {
          missingRecs = [...missingRecs, ' required attachment(s)'];
        }
        // GPA if required
        if (application.requiresGPA && !application.gradePointAverage) {
          missingRecs = [...missingRecs, ' GPA'];
        }
        break;
      }
      case 'TAS.ProgramType.3':
      case 'TAS.ProgramType.4.3':
        // Type 3/4.3: needs pre-approval attachments
        if (!application.hasRequiredPreApprovalAttachments) {
          missingRecs = [...missingRecs, ' required attachment(s)'];
        }
        break;
      default:
        break;
    }

    return missingRecs;
  }

  /**
   * Format missing requirements as user-friendly alert message
   */
  get missingRecsAlertForSubmitForUser(): string {
    return `You are missing the following requirement(s):${this.missingRequirementsForSubmitForUser}`;
  }

  /**
   * Determine which menu items should be disabled
   */
  get disabledMenuKeys(): string[] {
    const disabled: string[] = [];
    if (!this.canRejectApplication) {
      disabled.push('reject');
    }
    if (!this.isRejected) {
      disabled.push('reopen');
    }
    if (!this.canRequestChange) {
      disabled.push('requestChange');
    }
    if (!this.canSubmitForUser) {
      disabled.push('submitForUser');
    }
    if (!this.showRescindButton) {
      disabled.push('rescind');
    }
    if (!this.canReassignApprover) {
      disabled.push('reassign');
    }
    if (!this.canOverridePolicies) {
      disabled.push('policyOverride');
    }
    return disabled;
  }

  /**
   * Selects the correct adapter method based on application state
   */
  get rejectionCall() {
    return this.args.application.state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL'
      ? 'rejectCourseEvidence'
      : 'rejectCourses';
  }

  /**
   * Handle icon button click - opens the corresponding drawer
   */
  handleDrawerClick = (drawerType: string) => {
    this.args.onDrawerOpen(drawerType);
  };

  /**
   * Handle CourseCash icon click - delegates to external callback
   */
  handleCourseCashClick = () => {
    this.args.onCourseCashOpen?.();
  };

  get courseCashTooltip() {
    return htmlSafe(
      'CourseCa<span style="font-size:0.75em;vertical-align:baseline;display:inline-block">$</span>h Transactions'
    );
  }

  /**
   * Check if a specific drawer is currently active
   */
  isDrawerActive = (drawerType: string): boolean => {
    return this.args.activeDrawer === drawerType;
  };

  openRejectModal = () => {
    this.rejectNote = this.templateRejectComment;
    this.rejectError = null;
    this.isRejectModalOpen = true;
  };

  closeRejectModal = () => {
    this.isRejectModalOpen = false;
    this.rejectNote = '';
    this.rejectError = null;
  };

  updateRejectNote = (value: string) => {
    this.rejectNote = value;
  };

  openSubmitForUserModal = () => {
    this.submitForUserModalOpen = true;
    this.submitForUserError = null;
  };

  closeSubmitForUserModal = () => {
    this.submitForUserModalOpen = false;
    this.submitForUserError = null;
  };

  openRescindModal = () => {
    this.isRescindModalOpen = true;
    this.rescindError = null;
  };

  closeRescindModal = () => {
    this.isRescindModalOpen = false;
    this.rescindError = null;
  };

  openApproveModal = () => {
    this.isApproveModalOpen = true;
  };

  closeApproveModal = () => {
    this.isApproveModalOpen = false;
  };

  openRequestChangeModal = () => {
    this.isRequestChangeModalOpen = true;
    this.requestChangeNote = this.templateRequestChangeComment;
    this.requestChangeError = null;
  };

  closeRequestChangeModal = () => {
    this.isRequestChangeModalOpen = false;
    this.requestChangeNote = '';
    this.requestChangeError = null;
  };

  updateRequestChangeNote = (value: string) => {
    this.requestChangeNote = value;
  };

  openReassignDrawer = () => {
    this.args.onDrawerOpen('reassign-approver');
  };

  handleReassignClick = (approver: ApplicationApprovalSummaryApprover) => {
    this.selectedApproverToReassign = approver;
    this.isReassignModalOpen = true;
  };

  closeReassignModal = () => {
    this.isReassignModalOpen = false;
    this.selectedApproverToReassign = null;
  };

  openReopenModal = () => {
    this.isReopenModalOpen = true;
    this.reopenError = null;
  };

  closeReopenModal = () => {
    this.isReopenModalOpen = false;
    this.reopenError = null;
  };

  openOverrideModal = () => {
    this.isOverrideModalOpen = true;
  };

  closeOverrideModal = () => {
    this.isOverrideModalOpen = false;
  };

  confirmPolicyOverrideTask = dropTask(async () => {
    const today = this.intl.formatDate(new Date(), {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
    });

    const changelogEntry = `Ignore Policies set by ${this.sessionContext.user.person.fullName} on ${today}`;
    this.args.application.ignorePolicies = true;

    try {
      await this.args.application.save({ adapterOptions: { changelog: changelogEntry } });
      this.isOverrideModalOpen = false;
    } catch {
      this.args.application.ignorePolicies = false;
    }
  });

  openMoreActionsPopover = () => {
    this.isMoreActionsPopoverOpen = true;
  };

  closeMoreActionsPopover = () => {
    this.isMoreActionsPopoverOpen = false;
  };

  handleMoreActionsMenu = (key: string) => {
    this.closeMoreActionsPopover();

    // Modals use @renderInPlace={{true}} to avoid portal nesting issues with the popover
    // See: https://github.com/josemarluedke/frontile/issues/431
    switch (key) {
      case 'reject':
        this.openRejectModal();
        break;
      case 'reopen':
        this.openReopenModal();
        break;
      case 'requestChange':
        this.openRequestChangeModal();
        break;
      case 'submitForUser':
        this.openSubmitForUserModal();
        break;
      case 'rescind':
        this.openRescindModal();
        break;
      case 'reassign':
        this.openReassignDrawer();
        break;
      case 'policyOverride':
        this.openOverrideModal();
        break;
    }
  };

  /**
   * Mirrors the employee submit flow (prepareTotalsMiscFieldsAndSave + validateLimits)
   * for DRAFT (DEFINE_COURSES) state submissions on behalf of the user.
   */
  private async prepareDraftSubmission(application: TasApplicationModel) {
    const template = application.tasProgramInstance?.tasProgramTemplate;
    if (!template) {
      throw new Error(
        'Cannot prepare draft submission because tasProgramTemplate is missing for the application.'
      );
    }

    // 1. Set SEMESTER_CODE from COURSES_BEGIN_DATE
    if (template.semesterCodeIsVisible) {
      const beginDate = getSingleValueForTasField('COURSES_BEGIN_DATE', application.fields);
      if (beginDate) {
        setSingleValueForTasField(
          'SEMESTER_CODE',
          setSemesterCodeString(String(beginDate)),
          application.fields
        );
      }
    }

    // 2. Set payment method for direct pay programs
    const directPayPaymentMethod = template.isDirectPayProgram
      ? application.paymentMethod ||
        getSingleValueForTasField('PAYMENT_TYPE_FOR_DIRECT_PAY', application.fields) ||
        'TAS.AssistanceManner.COURSE_CASH'
      : application.paymentMethod;

    if (template.isDirectPayProgram) {
      setSingleValueForTasField(
        'PAYMENT_TYPE_FOR_DIRECT_PAY',
        directPayPaymentMethod,
        application.fields
      );
    }

    // 3. Set financial totals (mirrors setSubtotals / approved totals logic)
    const typeClassification = application.typeClassification;
    const isCourseCash =
      template.isDirectPayProgram && directPayPaymentMethod === 'TAS.AssistanceManner.COURSE_CASH';

    // Determine which total fields to use based on type
    const usesFinal =
      typeClassification === 'TAS.ProgramType.2' || typeClassification === 'TAS.ProgramType.4.2';

    if (isCourseCash) {
      // Course Cash: set card and non-card subtotals
      const courseCashField = usesFinal ? 'CARD_FINAL_APPROVED_TOTAL' : 'CARD_PRE_APPROVED_TOTAL';
      const nonCourseCashField = usesFinal ? 'FINAL_APPROVED_TOTAL' : 'PRE_APPROVED_TOTAL';

      setSingleValueForTasField(
        courseCashField,
        application.requestedCourseCashSubtotalLessScholarships,
        application.fields
      );
      setSingleValueForTasField(
        nonCourseCashField,
        application.requestedNonCourseCashSubtotal,
        application.fields
      );
    } else {
      // Non-Course Cash: clear card totals, set approved total
      setSingleValueForTasField('CARD_PRE_APPROVED_TOTAL', null, application.fields);
      setSingleValueForTasField('CARD_FINAL_APPROVED_TOTAL', null, application.fields);

      const approvedField = usesFinal ? 'FINAL_APPROVED_TOTAL' : 'PRE_APPROVED_TOTAL';
      let requestedAmount = application.requestedTotal;

      // Claims financials: total is always 0 at submission
      if (template.calculateTotalByClaimsFinancials) {
        requestedAmount = 0;
      } else if (application.requiresGPA) {
        // GPA-based: cap at GPA tier amount (only Type 2 / 4.2)
        const gpa = Number(
          getSingleValueForTasField('GRADE_POINT_AVERAGE', application.fields) || 0
        );
        const gpaAmount = this.tuitionAssistance.setTotalBasedOnGPA(gpa);
        requestedAmount = Math.min(gpaAmount, application.requestedTotal);
      }

      setSingleValueForTasField(approvedField, requestedAmount, application.fields);
    }

    // 4. Save the application with computed fields
    await application.save();
  }

  submitForUserTask = dropTask(async () => {
    this.submitForUserError = null;

    try {
      const application = this.args.application;
      const isDraft = application.state === 'TAS.ApplicationState.DEFINE_COURSES';

      // For DRAFT state, mirror the employee submit flow
      if (isDraft) {
        await this.prepareDraftSubmission(application);

        // Validate limits before submitting
        const employee = application.tasProgramInstance?.employee;
        if (employee) {
          const limitsResult =
            await this.tuitionAssistance.validateAmountRequestedAgainstCurrentLimitsStatus(
              application,
              employee
            );
          if (!limitsResult.can_submit) {
            const reductionAmount = application.isCreditBased
              ? `${limitsResult.reduction_needed} credits`
              : this.intl.formatNumber(limitsResult.reduction_needed / 100, {
                  style: 'currency',
                  currency: 'USD',
                });
            this.submitForUserError = `The requested amount exceeds the employee's remaining benefit. Please reduce by ${reductionAmount}.`;
            return;
          }
        }
      }

      // Call the appropriate adapter method based on state and program type
      const adapter = this.store.adapterFor('tas-application');
      const methodName = this.submitForUserAdapterMethod;

      // @ts-expect-error - Dynamic adapter method call
      await adapter[methodName](application);

      // Reload application to reflect state change
      await application.reload();

      // Close modal on success
      this.closeSubmitForUserModal();
    } catch (e) {
      console.error(e);
      this.submitForUserError = e instanceof Error ? e.message : String(e);
    }
  });

  rescindStateAction = dropTask(async () => {
    this.rescindError = null;

    try {
      const adapter = this.store.adapterFor('tas-application');

      // Call backend to rescind the last approval action
      // @ts-expect-error - Legacy adapter method
      await adapter.rescindLastApproverAction(this.args.application);

      // Reload application to reflect backend changes
      await this.args.application.reload();

      // Close modal on success
      this.closeRescindModal();
    } catch (e) {
      console.error(e);
      this.rescindError = e instanceof Error ? e.message : String(e);
    }
  });

  /**
   * Submit Request Change action
   * Calls adapter method with application and comment
   * Closes modal and refreshes page on success
   */
  confirmRequestChangeTask = dropTask(async () => {
    this.requestChangeError = null;

    try {
      const adapter = this.store.adapterFor('tas-application');
      const methodName = this.requestChangeAdapterMethod;

      // Call the appropriate adapter method dynamically
      // @ts-expect-error - Dynamic method call, adapter has these methods
      await adapter[methodName](this.args.application, this.requestChangeNote);

      // Success - close modal and refresh
      this.closeRequestChangeModal();

      // Reload the application to get updated state
      await this.args.application.reload({
        include: 'tas-assistances,tas-program-instance.employee.tas-participant',
      });

      // Show success feedback (optional - can add toast/alert later)
      console.log('Request Change submitted successfully');
    } catch (e) {
      console.error('Error submitting Request Change:', e);
      this.requestChangeError = e instanceof Error ? e.message : String(e);
    }
  });

  confirmRejectionTask = dropTask(async () => {
    this.rejectError = null;

    try {
      // Call rejection adapter method
      // @ts-expect-error - Legacy adapter method, will be migrated
      await this.store
        .adapterFor('tas-application')
        [this.rejectionCall](this.args.application, this.rejectNote);

      // Hold participant if repayment is involved
      if (this.args.application.canRequestRepayment) {
        const participantId = this.args.application.tasProgramInstance.employee.tasParticipant.id;
        await this.store
          .adapterFor('tas-participant')
          // @ts-expect-error - Legacy adapter method, will be migrated
          .holdTasParticipant(participantId, { note: this.rejectNote });
      }

      // Close modal and reload application history
      this.closeRejectModal();
      await this.args.application.loadHistory();
    } catch (e) {
      console.error(e);
      this.rejectError = e;
    }
  });

  /**
   * Reopen a rejected application
   * Calls the appropriate adapter method based on rejection state
   * Resets approved total if application was auto-rejected from ATTEND
   */
  reopenApplication = dropTask(async () => {
    this.reopenError = null;
    // @ts-expect-error - setAdapter can be undefined but is safe here as isRejected guard prevents invalid states
    const adapter = this.store.adapterFor(this.setAdapter);

    try {
      // Call the appropriate adapter method to reopen
      // @ts-expect-error - Dynamic method call on adapter
      await adapter[this.adapterMethod](this.args.application);

      // If the application was auto-rejected from the attend state
      if (this.lastTransitionLogAutoRejectedFromAttend) {
        // reset the approved total to the amount before the auto-rejection
        // @ts-expect-error - updateApprovedTotal may not be fully typed
        await adapter.updateApprovedTotal(
          this.args.application,
          this.lastApprovedTotalBeforeRejection,
          'Reset total to amount prior to auto-rejection'
        );
      }

      // Reload application to reflect state change
      await this.args.application.reload();

      // Close modal on success
      this.closeReopenModal();
    } catch (e) {
      console.error(e);
      this.reopenError = e instanceof Error ? e.message : String(e);
    }
  });

  <template>
    <div
      class="sticky bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg rounded-b-lg"
      data-test-approval-footer
      ...attributes
    >
      <div class="mx-auto px-2 py-2">
        {{! Unified Layout: Responsive flex container }}
        <div class="flex flex-col md:flex-row justify-between items-stretch md:items-center gap-3">
          {{! Primary Approve Button }}
          <Button
            @intent="primary"
            @size="sm"
            @isRunning={{@approveButtonLoading}}
            @isDisabled={{not this.canApproveApplication}}
            class="md:w-auto w-full"
            {{on "click" this.openApproveModal}}
            data-test-approve-button
          >
            <span class="hidden md:inline">Approve Application</span>
            <span class="md:hidden">Approve</span>
          </Button>

          {{! Icon Buttons Row - responsive sizing and spacing }}
          <div class="flex items-center justify-around md:justify-end gap-1 md:gap-1">
            {{#if this.showAiAnalysis}}
              <TooltipButton
                @tooltip={{t "tuition_assistance.application_approval.ai_analysis"}}
                @tooltipPlacement="top"
                @appearance="minimal"
                @intent="default"
                @size="sm"
                class="!p-2 !rounded-md"
                @isActive={{this.isDrawerActive "ai-analysis"}}
                data-test-button="footer-ai-analysis-button"
                {{on "click" (fn this.handleDrawerClick "ai-analysis")}}
              >
                <PhSparkle @size="20" class="md:hidden" />
                <PhSparkle @size="24" class="hidden md:block" />
              </TooltipButton>
            {{/if}}

            <TooltipButton
              @tooltip={{t "tuition_assistance.application_approval.comments"}}
              @tooltipPlacement="top"
              @appearance="minimal"
              @intent="default"
              @size="sm"
              class="!p-2 !rounded-md"
              @isActive={{this.isDrawerActive "comments"}}
              data-test-button="footer-comments-button"
              {{on "click" (fn this.handleDrawerClick "comments")}}
            >
              <PhChatCenteredText @size="20" class="md:hidden" />
              <PhChatCenteredText @size="24" class="hidden md:block" />
            </TooltipButton>

            <TooltipButton
              @tooltip={{if @expenseType @expenseType "Courses"}}
              @tooltipPlacement="top"
              @appearance="minimal"
              @intent="default"
              @size="sm"
              class="!p-2 !rounded-md"
              @isActive={{this.isDrawerActive "course-details"}}
              data-test-button="footer-course-details-button"
              {{on "click" (fn this.handleDrawerClick "course-details")}}
            >
              <PhStudent @size="20" class="md:hidden" />
              <PhStudent @size="24" class="hidden md:block" />
            </TooltipButton>

            <TooltipButton
              @tooltip={{t "tuition_assistance.application_approval.partner_information"}}
              @tooltipPlacement="top"
              @appearance="minimal"
              @intent="default"
              @size="sm"
              class="!p-2 !rounded-md"
              @isActive={{this.isDrawerActive "employer-info"}}
              data-test-button="footer-partner-info-button"
              {{on "click" (fn this.handleDrawerClick "employer-info")}}
            >
              <PhBuilding @size="20" class="md:hidden" />
              <PhBuilding @size="24" class="hidden md:block" />
            </TooltipButton>

            <TooltipButton
              @tooltip={{t "tuition_assistance.application_approval.financial_details"}}
              @tooltipPlacement="top"
              @appearance="minimal"
              @intent="default"
              @size="sm"
              class="!p-2 !rounded-md"
              @isActive={{this.isDrawerActive "financial-details"}}
              data-test-button="footer-financial-details-button"
              {{on "click" (fn this.handleDrawerClick "financial-details")}}
            >
              <PhCurrencyCircleDollar @size="20" class="md:hidden" />
              <PhCurrencyCircleDollar @size="24" class="hidden md:block" />
            </TooltipButton>

            <TooltipButton
              @tooltip="Attachments"
              @tooltipPlacement="top"
              @appearance="minimal"
              @intent="default"
              @size="sm"
              class="!p-2 !rounded-md"
              @isActive={{this.isDrawerActive "attachments"}}
              data-test-button="footer-attachments-button"
              {{on "click" (fn this.handleDrawerClick "attachments")}}
            >
              <PhFile @size="20" class="md:hidden" />
              <PhFile @size="24" class="hidden md:block" />
            </TooltipButton>

            <TooltipButton
              @tooltip={{t "tuition_assistance.application_approval.timeline"}}
              @tooltipPlacement="top"
              @appearance="minimal"
              @intent="default"
              @size="sm"
              class="!p-2 !rounded-md"
              @isActive={{this.isDrawerActive "timeline"}}
              data-test-button="footer-timeline-button"
              {{on "click" (fn this.handleDrawerClick "timeline")}}
            >
              <PhCalendarDots @size="20" class="md:hidden" />
              <PhCalendarDots @size="24" class="hidden md:block" />
            </TooltipButton>

            {{#if this.showCourseCash}}
              <TooltipButton
                @tooltip={{this.courseCashTooltip}}
                @tooltipPlacement="top"
                @appearance="minimal"
                @intent="default"
                @size="sm"
                class="!p-2 !rounded-md"
                @isActive={{this.isDrawerActive "course-cash"}}
                data-test-button="footer-course-cash-button"
                {{on "click" this.handleCourseCashClick}}
              >
                <PhCreditCard @size="20" class="md:hidden" />
                <PhCreditCard @size="24" class="hidden md:block" />
              </TooltipButton>
            {{/if}}

            {{! More Actions Menu - Popover with Listbox on desktop }}
            <div class="hidden md:block md:mr-16">
              <Popover
                @placement="top-end"
                @isOpen={{this.isMoreActionsPopoverOpen}}
                @onOpen={{this.openMoreActionsPopover}}
                @onClose={{this.closeMoreActionsPopover}}
              >
                <:trigger as |pop|>
                  <Button
                    @appearance="minimal"
                    @intent="default"
                    @size="sm"
                    class="!p-2 !rounded-md hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
                    {{pop.register}}
                    {{on "click" this.openMoreActionsPopover}}
                    data-test-footer-more-actions-button
                  >
                    <PhDotsThree
                      @size="24"
                      class="text-gray-600 hover:text-purple-600 dark:hover:text-purple-400"
                    />
                  </Button>
                </:trigger>

                <:body>
                  <Listbox
                    @intent="primary"
                    @onAction={{this.handleMoreActionsMenu}}
                    @disabledKeys={{this.disabledMenuKeys}}
                    as |l|
                  >
                    <l.Item @key="requestChange" data-test-footer-request-change-button>
                      <span class="flex items-center gap-2">
                        <PhArrowsCounterClockwise @size="16" />
                        Request Change
                      </span>
                    </l.Item>

                    <l.Item @key="submitForUser" data-test-footer-submit-for-user-button>
                      <span class="flex items-center gap-2">
                        <PhHandDeposit @size="16" />
                        Submit For User
                      </span>
                    </l.Item>

                    <div class="border-t border-gray-200 my-1"></div>

                    <l.Item @key="reassign" data-test-footer-reassign-button>
                      <span class="flex items-center gap-2">
                        <PhUserSwitch @size="16" />
                        {{t "tuition_assistance.approval_queue.approval_status"}}
                      </span>
                    </l.Item>

                    <l.Item @key="rescind" data-test-footer-rescind-button>
                      <span class="flex items-center gap-2">
                        <PhArrowUUpLeft @size="16" />
                        Undo Last
                      </span>
                    </l.Item>

                    <div class="border-t border-gray-200 my-1"></div>

                    {{! Show Reopen if rejected, otherwise show Reject }}
                    {{#if this.isRejected}}
                      <l.Item @key="reopen" @intent="primary" data-test-footer-reopen-button>
                        <span class="flex items-center gap-2">
                          <PhClockCounterClockwise @size="16" />
                          Reopen Application
                        </span>
                      </l.Item>
                    {{else}}
                      <l.Item @key="reject" @intent="danger" data-test-footer-reject-button>
                        <span class="flex items-center gap-2">
                          <PhXCircle @size="16" />
                          {{t "tuition_assistance.application_details.reject_application"}}
                        </span>
                      </l.Item>
                    {{/if}}

                    {{#if this.features.isAdminApp}}
                      <div class="border-t border-gray-200 my-1"></div>
                      <l.Item
                        @key="policyOverride"
                        data-test-footer-policy-override-button
                        class="hover:!text-red-600 hover:!bg-red-50 dark:hover:!bg-red-900/20"
                      >
                        <span class="flex items-center gap-2">
                          <PhShieldWarning @size="16" />
                          {{t
                            "common.tuition_assistance.application_details.policy_override_prompt"
                          }}
                        </span>
                      </l.Item>
                    {{/if}}
                  </Listbox>
                </:body>
              </Popover>
            </div>

            {{! More Actions - simple button for mobile }}
            <Button
              @appearance="minimal"
              @intent="default"
              @size="sm"
              class="md:hidden !p-2 !rounded-md hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors justify-center"
              @isDisabled={{not (or this.canRejectApplication this.isRejected)}}
              {{on "click" (if this.isRejected this.openReopenModal this.openRejectModal)}}
              data-test-footer-more-actions-button-mobile
            >
              <PhDotsThree
                @size="20"
                class="text-gray-600 hover:text-purple-600 dark:hover:text-purple-400"
              />
            </Button>
          </div>
        </div>
      </div>
    </div>

    {{! Rejection Modal }}
    <Modal
      @isOpen={{this.isRejectModalOpen}}
      @onClose={{this.closeRejectModal}}
      class="min-h-64"
      data-test-reject-modal
      @ariaLabel={{t "common.aria_labels.reject_application"}}
    >
      <:header> {{t "tuition_assistance.application_details.reject_application"}}</:header>
      <:body>
        {{#if @application.canRequestRepayment}}
          <div class="flex flex-row">
            <p class="text-xl my-2 text-tio-gray-800">
              {{t "tuition_assistance.application_details.repayment_amount"}}
            </p>

            <p class="text-xl my-4 text-tio-gray-800 ml-auto">
              {{safeFormatNumber
                (divide this.requestedRepaymentAmount 100)
                style="currency"
                currency="USD"
              }}</p>
          </div>
          {{#if @application.isPrepaidProgram}}
            <AlertBanner @intent="info">
              <p>
                {{t "tuition_assistance.application_details.rejecting_prepaid_initiates"}}
              </p>
            </AlertBanner>
          {{/if}}
        {{/if}}
        {{#if this.rejectTextAreaOptions.length}}
          <SelectTextArea
            @options={{this.rejectTextAreaOptions}}
            @optionLabel="title"
            @optionValue="body"
            @value={{this.rejectNote}}
            @onChange={{this.updateRejectNote}}
            @placeholder="Select a response or enter custom feedback..."
            @multiSelect={{true}}
          />
        {{else}}
          <Textarea
            @value={{this.rejectNote}}
            @onChange={{this.updateRejectNote}}
            placeholder="Enter feedback for the employee..."
            data-test-reject-note
          />
        {{/if}}
        {{#if this.rejectError}}
          <TioErrorMessages
            @showErrors={{true}}
            @error={{this.rejectError}}
            class="text-sm flex gap-2 items-center"
            @icon="warning"
          />
        {{/if}}
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.confirmRejectionTask.isRunning}}
          @submitText="Submit"
          @onSubmit={{this.confirmRejectionTask.perform}}
          @onCancel={{this.closeRejectModal}}
          @submitDisabled={{not this.rejectNote}}
        />
      </:footer>
    </Modal>

    {{! Submit for User Modal }}
    <Modal
      @isOpen={{this.submitForUserModalOpen}}
      @onClose={{this.closeSubmitForUserModal}}
      class="min-h-64"
      data-test-submit-for-user-modal
      @ariaLabel={{t "common.aria_labels.submit_application_for_user"}}
    >
      <:header>
        {{t "tuition_assistance.application_approval.submit_for_user.title"}}
      </:header>
      <:body>
        <div class="flex flex-col gap-4">
          <p class="text-sm text-gray-600">
            {{t "tuition_assistance.application_approval.submit_for_user.description"}}
          </p>

          {{#if
            (and
              (eq @application.state "TAS.ApplicationState.DEFINE_COURSES")
              @application.tasProgramInstance.tasProgramTemplate.isDirectPayProgram
            )
          }}
            <AlertBanner @intent="info">
              {{#if this.isCourseCashPayment}}
                {{t "tuition_assistance.application_approval.submit_for_user.course_cash_info"}}
              {{else}}
                {{t "tuition_assistance.application_approval.submit_for_user.reimbursement_info"}}
              {{/if}}
            </AlertBanner>

            {{#if this.isDraftCourseCashWithoutCardholder}}
              <AlertBanner @intent="warning">
                {{t "tuition_assistance.application_approval.submit_for_user.missing_cardholder"}}
              </AlertBanner>
            {{/if}}
          {{/if}}

          {{#if this.missingRequirementsForSubmitForUser.length}}
            <TioErrorMessages
              @showErrors={{true}}
              @error={{this.missingRecsAlertForSubmitForUser}}
              class="text-sm flex gap-2 items-center"
              @icon="warning"
            />
          {{/if}}

          {{#if this.submitForUserError}}
            <TioErrorMessages
              @showErrors={{true}}
              @error={{this.submitForUserError}}
              class="text-sm flex gap-2 items-center"
              @icon="warning"
            />
          {{/if}}
        </div>
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.submitForUserTask.isRunning}}
          @submitText={{t "common.submit"}}
          @cancelText={{t "common.cancel"}}
          @onSubmit={{this.submitForUserTask.perform}}
          @onCancel={{this.closeSubmitForUserModal}}
          @submitDisabled={{or
            (gt this.missingRequirementsForSubmitForUser.length 0)
            this.isDraftCourseCashWithoutCardholder
          }}
        />
      </:footer>
    </Modal>

    {{! Rescind Modal }}
    <Modal
      @isOpen={{this.isRescindModalOpen}}
      @onClose={{this.closeRescindModal}}
      class="min-h-64"
      data-test-rescind-modal
      @ariaLabel={{t "common.aria_labels.rescind"}}
    >
      <:header>
        Rescind
      </:header>
      <:body>
        <p class="text-lg font-semibold text-tio-gray-800">
          {{t "tuition_assistance.application_details.rescind.body"}}
        </p>
        <p class="my-4 text-tio-gray-800">
          {{t "tuition_assistance.application_details.rescind.footnote"}}
        </p>

        {{#if this.rescindError}}
          <TioErrorMessages
            @showErrors={{true}}
            @error={{this.rescindError}}
            class="text-sm flex gap-2 items-center"
            @icon="warning"
          />
        {{/if}}
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.rescindStateAction.isRunning}}
          @submitText="Confirm"
          @onSubmit={{this.rescindStateAction.perform}}
          @onCancel={{this.closeRescindModal}}
        />
      </:footer>
    </Modal>

    {{! Request Change Modal }}
    <Modal
      @isOpen={{this.isRequestChangeModalOpen}}
      @onClose={{this.closeRequestChangeModal}}
      class="min-h-64"
      data-test-request-change-modal
      @ariaLabel={{t "common.aria_labels.request_change"}}
    >
      <:header>
        Request Change
      </:header>
      <:body>
        <p class="text-sm text-gray-600 mb-4">
          Send this application back to the employee with feedback for revision.
        </p>

        {{#if this.requestChangeTextAreaOptions.length}}
          <SelectTextArea
            @options={{this.requestChangeTextAreaOptions}}
            @optionLabel="title"
            @optionValue="body"
            @value={{this.requestChangeNote}}
            @onChange={{this.updateRequestChangeNote}}
            @placeholder="Select a response or enter custom feedback..."
            @multiSelect={{true}}
          />
        {{else}}
          <Textarea
            @value={{this.requestChangeNote}}
            @onChange={{this.updateRequestChangeNote}}
            placeholder="Enter feedback for the employee..."
            data-test-request-change-note
            class="min-h-32"
          />
        {{/if}}

        {{#unless this.requestChangeNote}}
          <p class="text-sm text-red-600 dark:text-red-400 mt-2">
            A comment is required before submitting.
          </p>
        {{/unless}}

        {{#if this.requestChangeError}}
          <TioErrorMessages
            @showErrors={{true}}
            @error={{this.requestChangeError}}
            class="text-sm flex gap-2 items-center mt-2"
            @icon="warning"
          />
        {{/if}}
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.confirmRequestChangeTask.isRunning}}
          @submitText="Submit"
          @onSubmit={{this.confirmRequestChangeTask.perform}}
          @onCancel={{this.closeRequestChangeModal}}
          @submitDisabled={{not this.requestChangeNote}}
        />
      </:footer>
    </Modal>

    {{! Reopen Modal }}
    <Modal
      @isOpen={{this.isReopenModalOpen}}
      @onClose={{this.closeReopenModal}}
      @size="md"
      data-test-reopen-modal
      @ariaLabel={{t "common.aria_labels.reopen_application"}}
    >
      <:header>
        Reopen Application
      </:header>
      <:body>
        <p class="text-gray-700 mb-4">
          Are you sure you want to reopen this rejected application? This will return the
          application to a pending state for review.
        </p>

        {{#if this.reopenError}}
          <AlertBanner @intent="warning" class="mt-4">
            <TioErrorMessages @showErrors={{true}} @error={{this.reopenError}} />
          </AlertBanner>
        {{/if}}
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.reopenApplication.isRunning}}
          @submitText="Reopen Application"
          @cancelText="Cancel"
          @onSubmit={{this.reopenApplication.perform}}
          @onCancel={{this.closeReopenModal}}
          @submitIntent="primary"
          @submitDisabled={{this.reopenApplication.isRunning}}
        />
      </:footer>
    </Modal>

    {{! Approve Modal - Unmount/remount to reset state }}
    {{#if this.isApproveModalOpen}}
      <ApproveModal
        @show={{this.isApproveModalOpen}}
        @onClose={{this.closeApproveModal}}
        @application={{@application}}
      />
    {{/if}}

    {{! Policy Override Modal }}
    <Modal
      @isOpen={{this.isOverrideModalOpen}}
      @onClose={{this.closeOverrideModal}}
      class="min-h-64"
      data-test-policy-override-modal
      @ariaLabel={{t "common.aria_labels.override_policies"}}
    >
      <:header>
        {{t "common.tuition_assistance.application_details.policy_override_confirm"}}
      </:header>
      <:body>
        <p class="text-gray-700 mb-4">
          {{t "common.tuition_assistance.application_details.policy_override_description"}}
        </p>
        <p class="font-semibold italic text-gray-700">
          {{t "common.tuition_assistance.application_details.policy_override_warning"}}
        </p>
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.confirmPolicyOverrideTask.isRunning}}
          @onCancel={{this.closeOverrideModal}}
          @onSubmit={{this.confirmPolicyOverrideTask.perform}}
          @submitText={{t "common.confirm"}}
          @submitIntent="danger"
        />
      </:footer>
    </Modal>
  </template>
}

export default ApprovalFooter;
